// =================================================================================
// File: src/pages/ExplorePage.jsx
// Description: Full-page interactive world map for exploring ceramic studios
// STUDIO WORLD: Clicking pins opens studio website in new tab
// =================================================================================

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePublicStudio } from "../contexts/PublicStudioContext";
import MudoWorldMap from "../components/Map/MudoWorldMap";
import { ArrowLeft, Search, X } from "lucide-react";

export default function ExplorePage() {
  const navigate = useNavigate();
  const { allPublicStudios = [], loading } = usePublicStudio() || {};
  const [searchQuery, setSearchQuery] = useState("");

  // Filter studios by search
  const filteredStudios = searchQuery.trim()
    ? allPublicStudios.filter((s) =>
        (s.display_name || s.name || "").toLowerCase().includes(searchQuery.toLowerCase())
      )
    : allPublicStudios;

  // Convert studios to map locations
  const locations = filteredStudios
    .filter((s) => s.lat && s.lng) // Only include studios with coordinates
    .map((s) => ({
      id: s.studio_id || s.id,
      name: s.display_name || s.name || "Unnamed Studio",
      type: "studio",
      lat: s.lat,
      lng: s.lng,
      slug: s.slug,
      website_url: s.website,
    }));

  const handleLocationClick = (studio) => {
    // In studio world, pins open websites - this is just a fallback
    console.log("[ExplorePage] Location clicked:", studio.name);
  };

  return (
    <div className="h-screen flex flex-col bg-neutral-900 text-white">
      {/* Header */}
      <div className="bg-neutral-800 border-b border-neutral-700 p-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate("/dashboard")}
            className="p-2 rounded-lg hover:bg-neutral-700 transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft size={20} />
          </button>

          <h1 className="text-xl font-bold">Explore Ceramic Studios</h1>

          <div className="flex-1 max-w-md ml-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" size={18} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search studios..."
                className="w-full pl-10 pr-10 py-2 rounded-lg bg-neutral-700 border border-neutral-600 focus:border-blue-500 focus:outline-none"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          <div className="text-sm text-neutral-400">
            {loading ? "Loading..." : `${locations.length} studios`}
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex-1 relative">
        <MudoWorldMap
          locations={locations}
          initialCenter={[20, 0]}
          initialZoom={2}
          onLocationClick={handleLocationClick}
          worldMode="studio"
          className="h-full w-full"
          disableAutoFit={true}
        />
      </div>
    </div>
  );
}
